'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import RichTextTitle from '../rich-text-title';
import SlideshowCard from './slideshow-card';
import SlideshowProgress from './slideshow-progress';
import useSlideshow from './use-slideshow';
import type { SlideshowCardData } from './types';

const MAX_CARDS = 5;

export type Props = {
	title: string;
	cards: SlideshowCardData[];
	id?: string;
	interactionType?: string;
	autoPlay?: boolean;
	className?: string;
};

export default function SignatureSlideshow({
	title,
	cards,
	id,
	interactionType,
	autoPlay = true,
	className,
}: Props): ReactElement | null {
	const slides = cards.slice(0, MAX_CARDS);
	const count = slides.length;
	const { trackLink } = useTealium();

	const {
		containerRef,
		index,
		fill,
		isPlaying,
		shouldPlayVideo,
		goTo,
		togglePlayback,
		onTouchStart,
		onTouchEnd,
		onPointerDown,
		onPointerUp,
		onPointerCancel,
		onClickCapture,
		onDragStart,
	} = useSlideshow({
		count,
		autoPlay,
		onManualAdvance: interactionType
			? () =>
					trackLink('interaction', {
						interaction_type: interactionType,
						interaction_name: 'signature slideshow',
						interaction_detail: 'image carousel icon',
					})
			: undefined,
	});

	if (count === 0) return null;

	// The active card is centred in the viewport so the previous/next cards peek
	// in on both sides (Figma "Min - Max cards" + handoff frames). clamp() keeps
	// the first card flush-left and the last card flush-right instead of
	// over-scrolling past the ends. Percentages resolve against the track width
	// (the padded content box), the same basis as --card-w, so the maths lines up
	// on both breakpoints. On desktop --card-w is 100%, so (100% - --card-w) is 0
	// and this collapses back to a plain -index * step translation.
	const step = 'var(--card-w) + var(--card-gap)';
	const trackOffset = `clamp(calc((100% - var(--card-w)) - ${count - 1} * (${step})), calc((100% - var(--card-w)) / 2 - ${index} * (${step})), 0px)`;

	return (
		<section
			ref={containerRef}
			className={cn(
				// Title-to-carousel: Figma ".feature carousel" itemSpacing is bound to
				// heading-m on desktop (stack-l on mobile — same 20/30 curve).
				'flex flex-col items-center gap-y-heading-m',
				className
			)}
		>
			<RichTextTitle
				title={title}
				id={id}
				className="text-display3 w-full spacing-wide-grid text-text-heading md:text-center"
			/>

			<div className="flex w-full flex-col items-center gap-y-stack-l md:gap-y-stack-m">
				{/* Full-bleed clip box: the section stays uncapped and this box pads
				    in to the wide grid's content edge (spacing-wide-grid-bleed —
				    gutter plus the centring margin past each tier's cap, decision
				    b96ff0), so the peeking cards render into the page margin,
				    overflow-hidden only clips them at the viewport edge, and the
				    active card lines up with capped neighbours at every width
				    (IAI-329). --card-gap keeps its hardcoded 15px/30px, which is
				    already the correct grid-gutter value. */}
				<div
					className={cn(
						'w-full overflow-hidden spacing-wide-grid-bleed',
						'[--card-gap:15px] md:[--card-gap:30px] md:[--card-w:100%]',
						count > 1 ? '[--card-w:91.304%]' : '[--card-w:100%]'
					)}
				>
					<ul
						className="flex gap-[var(--card-gap)] transition-transform duration-[1200ms] ease-in-out motion-reduce:transition-none md:duration-[1500ms]"
						style={{
							transform: `translateX(${trackOffset})`,
						}}
						onTouchStart={onTouchStart}
						onTouchEnd={onTouchEnd}
						onPointerDown={onPointerDown}
						onPointerUp={onPointerUp}
						onPointerCancel={onPointerCancel}
						onClickCapture={onClickCapture}
						onDragStart={onDragStart}
					>
						{slides.map((card, slide) => (
							<SlideshowCard
								key={card.id}
								card={card}
								isActive={slide === index}
								shouldPlayVideo={shouldPlayVideo}
							/>
						))}
					</ul>
				</div>

				{count > 1 && (
					<SlideshowProgress
						count={count}
						index={index}
						fill={fill}
						isPlaying={isPlaying}
						onTogglePlayback={togglePlayback}
						onSelect={goTo}
					/>
				)}
			</div>
		</section>
	);
}
